import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { IncMoveCounter, SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import { Button } from '../../shared-module/buttons/button.def';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import {
  PuzzleKeyboardService,
  PuzzleSpecialKeys,
} from '../../shared-module/puzzle-softkeyboard/puzzle-keyboard.service';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';
import { FeedbackService } from '../../shared-module/feedback/feedback.service';
import { FeedbackFeatureEnum } from '../../shared-module/feedback/feedback-feature.enum';
import WordGuessPuzzleConfig from '../interfaces/word-guess-puzzle.config';
import WordGuessTile from '../interfaces/word-guess-tile';
import { WordGuessLetterStateEnum } from '../interfaces/word-guess-letter-state.enum';
import { WordGuessPuzzle, adaptWordGuessPuzzleConfig } from '../config/word-guess-puzzle-config.adapter';
import { WORD_GUESS_KEY_FILTER_REGEX, normalizeWordGuessLetter } from '../logic/word-guess-alphabet';
import { buildWordGuessKeyStates, evaluateWordGuess } from '../logic/word-guess-evaluator';
import {
  createWordGuessInput,
  deleteWordGuessLetter,
  hasWordGuessInput,
  isWordGuessInputComplete,
  moveWordGuessCursor,
  wordGuessInputToWord,
  writeWordGuessLetter,
} from '../logic/word-guess-cursor';
import { WordGuessDictionaryService } from '../dictionary/word-guess-dictionary.service';
import { WordGuessPuzzleState } from '../store/word-guess-puzzle.state';
import { WordGuessPuzzleModel } from '../store/word-guess-puzzle.model';
import { InitWordGuessPuzzle, PersistWordGuessState } from '../store/word-guess-puzzle.actions';
import { WordGuessKeyboardComponent } from '../keyboard/word-guess-keyboard.component';
import { WordGuessPuzzleWinScreenComponent } from '../win-screen/word-guess-puzzle-win-screen.component';
import { WordGuessHelpComponent } from '../help/word-guess-help.component';

const INVALID_GUESS_ANIMATION_MS = 600;
const REVEAL_ANIMATION_MS = 1400;

/*
 * Counted from the end of the game, so it covers the reveal plus enough time on the win
 * dialog for the player to have read it before the feedback modal stacks on top.
 */
const FEEDBACK_PROMPT_DELAY_MS = REVEAL_ANIMATION_MS + 10_000;

/* Kept in sync with the tile metrics in the stylesheet. */
const TILE_SIZE_PX = 58;
const TILE_GAP_PX = 6;

@Component({
  selector: 'lib-app-word-guess-puzzle-board',
  templateUrl: './word-guess-puzzle-board.component.html',
  styleUrls: ['./word-guess-puzzle-board.component.scss'],
  standalone: true,
  imports: [CommonModule, WordGuessKeyboardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WordGuessPuzzleBoardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gridWrapper', { static: true }) gridWrapper: ElementRef<HTMLElement>;

  rows: WordGuessTile[][] = [];
  keyStates: { [letter: string]: WordGuessLetterStateEnum } = {};
  showKeyboard = true;
  invalidRowIndex: number = null;
  revealRowIndex: number = null;
  solved = false;
  lost = false;

  /* Tile of the active row the next letter goes into. Only meaningful while the game runs. */
  cursorIndex = 0;

  /* The grid keeps its natural pixel size and is scaled down to fit small screens. */
  scaleValue = 1;
  gridWidthPX = 0;
  gridHeightPX = 0;

  private puzzle: WordGuessPuzzle;
  private puzzleID: string;
  private guesses: string[] = [];
  /* One slot per tile of the active row, '' where nothing has been typed yet. */
  private currentInput: string[] = [];
  private finishSignalled = false;

  private buttonBarSubscription: Subscription;
  private winDialogSubscription: Subscription;
  private helpDialogSubscription: Subscription;
  private puzzleEventsSubscription: Subscription;
  private gameStateSubscription: Subscription;
  private moduleStateSubscription: Subscription;
  private keyboardSubscriptions: Subscription[] = [];
  private resizeObserver: ResizeObserver;
  private feedbackPromptTimeout: number = null;

  constructor(
    private framework: PuzzleFrameworkService,
    private store: Store,
    private cd: ChangeDetectorRef,
    private winDialogService: PuzzleWinDialogService,
    private helpDialogService: PuzzleHelpDialogService,
    private keyboardService: PuzzleKeyboardService,
    private dictionary: WordGuessDictionaryService,
    private feedback: FeedbackService,
    /*
     * Not used here, but it must stay: PuzzleSettingsModalService subscribes to the button
     * bar in its own constructor, and being providedIn:'root' it is only constructed once
     * something injects it. Without this the settings entry in the menu does nothing.
     * Every other puzzle board carries the same injection for the same reason.
     */
    settingsModalService: PuzzleSettingsModalService
  ) {
    this.winDialogSubscription = this.winDialogService.winDialogRegistered.subscribe(() => {
      this.winDialogService.showButtons([
        PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
        PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
      ]);

      /*
       * A lost game still ends the puzzle, but posting the time of a failed attempt to the
       * leaderboard would rank players who never found the word.
       */
      this.winDialogService.setLeaderboardVisible(this.solved);

      this.winDialogService.injectComponent(WordGuessPuzzleWinScreenComponent, {
        solved: this.solved,
        solution: this.puzzle ? this.puzzle.solution : '',
        solutionOriginal: this.puzzle ? this.puzzle.solutionOriginal : undefined,
        guessCount: this.guesses.length,
        maxGuesses: this.puzzle ? this.puzzle.maxGuesses : 0,
      });
    });

    this.helpDialogSubscription = this.helpDialogService.helpDialogRegistered.subscribe(() => {
      const translationKey = this.framework.getTranslationKey() || 'WORD_GUESS';
      this.helpDialogService.injectHelpComponent(WordGuessHelpComponent, { translationKey });
    });

    this.buttonBarSubscription = this.framework.buttonPressed.subscribe((button) => {
      if (button === Button.Reset) {
        this.resetPuzzle();
      }
    });

    this.puzzleEventsSubscription = this.framework.puzzleEvents.subscribe((event) => {
      if (event === PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE) {
        this.solveCurrentPuzzle();
      }
    });
  }

  ngOnInit(): void {
    const puzzleConfig = this.framework.puzzleConfig.puzzle as WordGuessPuzzleConfig;

    try {
      this.puzzle = adaptWordGuessPuzzleConfig(puzzleConfig);
    } catch (error) {
      /* Surfaced through the developer error list of the puzzle screen. */
      this.framework.puzzleFileLoadErrors.push(error.message);
      console.error(error);
      return;
    }

    this.puzzleID = this.puzzle.puzzleID;
    this.showKeyboard = this.framework.puzzleFrameworkConfig.wordGuessKeyboardEnabled !== false;
    this.resetInput();

    this.store.dispatch(new SetPuzzleID({ puzzleID: this.puzzleID }));

    this.gameStateSubscription = this.store.select(PuzzleGameState.getByID(this.puzzleID)).subscribe((puzzleGame) => {
      if (puzzleGame === null) {
        void this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.WordGuessPuzzle, 0);
      }
    });

    this.moduleStateSubscription = this.store
      .select(WordGuessPuzzleState.getByID(this.puzzleID))
      .subscribe((wordGuessGame: WordGuessPuzzleModel) => {
        if (wordGuessGame === null) {
          this.initWordGuessRedux();
          return;
        }

        this.guesses = wordGuessGame.guesses || [];
        this.resetInput();
        this.refresh();
        this.checkForEnd();
      });

    this.registerKeyboard();

    if (this.framework.puzzleFrameworkConfig.wordGuessValidateGuesses !== false) {
      void this.dictionary.load(this.framework.puzzleFrameworkConfig.wordGuessDictionaryUrl);
    }

    this.gridWidthPX = this.puzzle.wordLength * (TILE_SIZE_PX + TILE_GAP_PX) - TILE_GAP_PX;
    this.gridHeightPX = this.puzzle.maxGuesses * (TILE_SIZE_PX + TILE_GAP_PX) - TILE_GAP_PX;

    this.refresh();
  }

  ngAfterViewInit(): void {
    if (!this.puzzle) {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.scaleGrid());
    this.resizeObserver.observe(this.gridWrapper.nativeElement);
    this.scaleGrid();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.cancelFeedbackPrompt();
    [
      this.buttonBarSubscription,
      this.winDialogSubscription,
      this.helpDialogSubscription,
      this.puzzleEventsSubscription,
      this.gameStateSubscription,
      this.moduleStateSubscription,
      ...this.keyboardSubscriptions,
    ].forEach((subscription) => subscription?.unsubscribe());
  }

  get canSubmit(): boolean {
    return !this.gameOver && isWordGuessInputComplete(this.currentInput);
  }

  get canDelete(): boolean {
    return !this.gameOver && hasWordGuessInput(this.currentInput);
  }

  get gameOver(): boolean {
    return this.solved || this.lost;
  }

  /* The row being typed into, or null once the game is over and nothing is editable. */
  get activeRowIndex(): number {
    return this.gameOver || !this.puzzle ? null : this.guesses.length;
  }

  onLetter(rawLetter: string): void {
    const letter = normalizeWordGuessLetter(rawLetter);

    if (!letter || this.gameOver || !this.puzzle) {
      return;
    }

    const { input, cursorIndex } = writeWordGuessLetter(this.currentInput, this.cursorIndex, letter);

    this.currentInput = input;
    this.cursorIndex = cursorIndex;
    this.refresh();
  }

  onDelete(): void {
    if (this.gameOver || !this.puzzle) {
      return;
    }

    const { input, cursorIndex } = deleteWordGuessLetter(this.currentInput, this.cursorIndex);

    this.currentInput = input;
    this.cursorIndex = cursorIndex;
    this.refresh();
  }

  /* Places the cursor. Submitted rows are history, only the active one takes input. */
  onTileClicked(rowIndex: number, tileIndex: number): void {
    if (rowIndex !== this.activeRowIndex) {
      return;
    }

    this.cursorIndex = moveWordGuessCursor(tileIndex, 0, this.currentInput.length);
    this.cd.markForCheck();
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      /* A gap somewhere in the row — nudge it instead of silently ignoring the key. */
      this.rejectGuess('WORD_GUESS.snack_bar.too_short');
      return;
    }

    const guess = wordGuessInputToWord(this.currentInput);

    if (
      this.framework.puzzleFrameworkConfig.wordGuessValidateGuesses !== false &&
      !this.dictionary.has(guess, this.puzzle.wordLength)
    ) {
      this.rejectGuess('WORD_GUESS.snack_bar.unknown_word');
      return;
    }

    this.guesses = [...this.guesses, guess];
    this.resetInput();
    this.revealRowIndex = this.guesses.length - 1;

    /*
     * Count the move first: persisting runs the state subscription synchronously, which can
     * already finish the game, and IncMoveCounter is a no-op on a finished puzzle — the last
     * guess would otherwise never be counted.
     */
    this.store.dispatch(new IncMoveCounter({ puzzleID: this.puzzleID }));
    this.store.dispatch(new PersistWordGuessState({ puzzleID: this.puzzleID, guesses: this.guesses }));

    this.refresh();
    this.checkForEnd();

    window.setTimeout(() => {
      this.revealRowIndex = null;
      this.cd.markForCheck();
    }, REVEAL_ANIMATION_MS);
  }

  /*
   * Shrinks the grid to whatever space is left above the keyboard. It never scales up, so
   * the tiles keep the size the customer configured on roomy screens.
   */
  private scaleGrid(): void {
    const wrapper = this.gridWrapper?.nativeElement;

    if (!wrapper || !this.gridWidthPX || !this.gridHeightPX) {
      return;
    }

    const availableWidth = wrapper.clientWidth;
    const availableHeight = wrapper.clientHeight;

    if (!availableWidth || !availableHeight) {
      return;
    }

    const scale = Math.min(1, availableWidth / this.gridWidthPX, availableHeight / this.gridHeightPX);

    if (scale !== this.scaleValue) {
      this.scaleValue = scale;
      this.cd.markForCheck();
    }
  }

  private registerKeyboard(): void {
    this.keyboardService.keyFilterRegex = WORD_GUESS_KEY_FILTER_REGEX;

    this.keyboardSubscriptions.push(
      this.keyboardService.onKeyDown.subscribe((key: string) => {
        if (key === '') {
          this.onDelete();
        } else {
          this.onLetter(key);
        }
        this.cd.detectChanges();
      })
    );

    this.keyboardSubscriptions.push(
      this.keyboardService.onEnter.subscribe(() => {
        this.onSubmit();
        this.cd.detectChanges();
      })
    );

    this.keyboardSubscriptions.push(
      this.keyboardService.onSpecialKeyDown.subscribe((key: PuzzleSpecialKeys) => {
        if (key !== PuzzleSpecialKeys.ArrowLeft && key !== PuzzleSpecialKeys.ArrowRight) {
          return;
        }

        this.moveCursor(key === PuzzleSpecialKeys.ArrowLeft ? -1 : 1);
        this.cd.detectChanges();
      })
    );
  }

  private moveCursor(offset: number): void {
    if (this.gameOver || !this.puzzle) {
      return;
    }

    this.cursorIndex = moveWordGuessCursor(this.cursorIndex, offset, this.currentInput.length);
  }

  /* Clears the active row and parks the cursor on its first tile. */
  private resetInput(): void {
    this.currentInput = createWordGuessInput(this.puzzle ? this.puzzle.wordLength : 0);
    this.cursorIndex = 0;
  }

  private initWordGuessRedux(): void {
    this.store.dispatch(new InitWordGuessPuzzle({ puzzleID: this.puzzleID, guesses: [] }));
  }

  private resetPuzzle(): void {
    this.cancelFeedbackPrompt();

    this.guesses = [];
    this.resetInput();
    this.solved = false;
    this.lost = false;
    this.finishSignalled = false;
    this.revealRowIndex = null;
    this.invalidRowIndex = null;

    void this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.WordGuessPuzzle, 0);
    this.framework.notifyParentResetGameData(this.puzzleID);
    this.initWordGuessRedux();
    this.refresh();
  }

  private solveCurrentPuzzle(): void {
    if (!this.puzzle || this.gameOver) {
      return;
    }

    this.guesses = [...this.guesses, this.puzzle.solution];
    this.resetInput();
    this.store.dispatch(new PersistWordGuessState({ puzzleID: this.puzzleID, guesses: this.guesses }));
    this.refresh();
    this.checkForEnd();
  }

  private rejectGuess(translationKey: string): void {
    this.invalidRowIndex = this.guesses.length;
    void this.framework.showTranslatedToast(translationKey);
    this.cd.markForCheck();

    window.setTimeout(() => {
      this.invalidRowIndex = null;
      this.cd.markForCheck();
    }, INVALID_GUESS_ANIMATION_MS);
  }

  /*
   * Rebuilds everything the template renders from the submitted guesses plus the row the
   * player is currently typing.
   */
  private refresh(): void {
    if (!this.puzzle) {
      return;
    }

    const rows: WordGuessTile[][] = [];

    for (let rowIndex = 0; rowIndex < this.puzzle.maxGuesses; rowIndex++) {
      const guess = this.guesses[rowIndex];

      if (guess !== undefined) {
        const states = evaluateWordGuess(guess, this.puzzle.solution);
        rows.push(guess.split('').map((letter, index) => ({ letter, state: states[index] })));
        continue;
      }

      const isCurrentRow = rowIndex === this.guesses.length;
      const input = isCurrentRow ? this.currentInput : [];

      rows.push(
        Array.from({ length: this.puzzle.wordLength }, (unused, index) => ({
          letter: input[index] || '',
          state: WordGuessLetterStateEnum.Empty,
        }))
      );
    }

    this.rows = rows;
    this.keyStates = buildWordGuessKeyStates(this.guesses, this.puzzle.solution);
    this.solved = this.guesses.indexOf(this.puzzle.solution) !== -1;
    this.lost = !this.solved && this.guesses.length >= this.puzzle.maxGuesses;

    this.cd.markForCheck();
  }

  private checkForEnd(): void {
    if (!this.puzzle) {
      return;
    }

    this.store.dispatch(
      new SetGameProgress({
        puzzleID: this.puzzleID,
        progress: this.gameOver ? 1 : this.guesses.length / this.puzzle.maxGuesses,
      })
    );

    if (!this.gameOver || this.finishSignalled) {
      return;
    }

    this.finishSignalled = true;

    /*
     * Give the last row time to flip before the framework opens the win dialog. The value
     * has to be set on both outcomes, it is a shared property of the dialog service.
     */
    this.winDialogService.solutionDialogDelay = REVEAL_ANIMATION_MS;
    this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));

    this.scheduleFeedbackPrompt();
  }

  /*
   * Asks how the round went once it is over. Whether anything is shown is decided by the
   * feedback service: the project config has to list the feature, the player must not have
   * answered before, and no other feedback modal may be open — the leaderboard prompt of a
   * daily puzzle can arrive around the same time.
   */
  private scheduleFeedbackPrompt(): void {
    this.cancelFeedbackPrompt();

    const solved = this.solved;
    const guessCount = this.guesses.length;
    const maxGuesses = this.puzzle.maxGuesses;

    this.feedbackPromptTimeout = window.setTimeout(() => {
      this.feedbackPromptTimeout = null;
      void this.feedback.maybePromptFeedbackModal(FeedbackFeatureEnum.WordGuess, {
        puzzleID: this.puzzleID,
        outcome: solved ? 'solved' : 'lost',
        guessCount,
        maxGuesses,
      });
    }, FEEDBACK_PROMPT_DELAY_MS);
  }

  private cancelFeedbackPrompt(): void {
    if (this.feedbackPromptTimeout !== null) {
      window.clearTimeout(this.feedbackPromptTimeout);
      this.feedbackPromptTimeout = null;
    }
  }
}
